// src/components/layout/Layout.jsx - Исправленная версия с CSS Grid
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import Sidebar from './Sidebar';
import './Layout.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import Loader from '../common/Loader';

/**
 * Основной компонент макета приложения с CSS Grid архитектурой
 */
const Layout = ({ 
  children, 
  className = '',
  style = {},
  showSidebar = null,
  showHeader = true,
  showFooter = true,
  headerVariant = 'default',
  containerProps = {}
}) => {
  const { t } = useTranslation();
  const { loading, isAuthenticated, user } = useAuth();
  const location = useLocation();

  // ✅ НОВОЕ: Локальное состояние для сворачивания sidebar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // ✅ АВТООПРЕДЕЛЕНИЕ containerType на основе маршрута
  const getContainerType = useCallback(() => {
    const path = location.pathname;
    
    // Discovery/Analysis страницы = широкий контейнер
    if (path.includes('discovery') || path.includes('analysis')) {
      return 'discovery';
    }
    
    // Dashboard страницы = полная ширина
    if (path.startsWith('/research/')) {
      return 'full';
    }
    
    // Формы и профили = средняя ширина  
    if (path.includes('profile') || path.includes('settings') || path.includes('subscription')) {
      return 'medium';
    }

    // Аутентификация = средняя ширина без sidebar
    if (path.includes('login') || path.includes('register') || path.includes('forgot') || path.includes('verify')) {
      return 'medium';
    }

    // Главная и публичные страницы = широкий
    if (path === '/' || path.includes('about') || path.includes('pricing')) {
      return 'wide';
    }
    
    // По умолчанию
    return 'default';
  }, [location.pathname]);

  // Проверяем, является ли текущая страница страницей аутентификации
  const isAuthPage = location.pathname.startsWith('/login') || 
                     location.pathname.startsWith('/register') ||
                     location.pathname.startsWith('/forgot-password') ||
                     location.pathname.startsWith('/password-reset') ||
                     location.pathname.startsWith('/verify-email') ||
                     location.pathname.startsWith('/resend-verification') ||
                     location.pathname.startsWith('/logout') ||
                     location.pathname.startsWith('/docs') ||
                     location.pathname.startsWith('/404') ||
                     location.pathname.startsWith('/pricing');

  // ✅ УПРОЩЕННАЯ ЛОГИКА: Определяем состояние sidebar
  const getSidebarState = useCallback(() => {
    // Если задано принудительное значение, используем его
    if (showSidebar !== null) {
      return {
        isVisible: showSidebar,
        isCollapsed: sidebarCollapsed
      };
    }
    
    // Если идет загрузка данных пользователя или пользователь НЕ авторизован
    if (loading || !isAuthenticated) {
      return {
        isVisible: false,
        isCollapsed: false
      };
    }
    
    // Если это страница аутентификации, не показываем sidebar
    if (isAuthPage) {
      return {
        isVisible: false,
        isCollapsed: false
      };
    }
    
    // Для авторизованных пользователей на обычных страницах показываем sidebar
    return {
      isVisible: true,
      isCollapsed: sidebarCollapsed
    };
  }, [showSidebar, loading, isAuthenticated, isAuthPage, sidebarCollapsed]);

  const sidebarState = getSidebarState();

  // ✅ CALLBACK для изменения состояния сворачивания
  const handleSidebarCollapsedChange = useCallback((collapsed) => {
    setSidebarCollapsed(collapsed);
  }, []);

  // ✅ ФОРМИРУЕМ КЛАСС ДЛЯ LAYOUT GRID
  const getLayoutClass = useCallback(() => {
    let layoutClass = 'layout';
    
    // Определяем состояние sidebar для CSS Grid
    if (!sidebarState.isVisible) {
      layoutClass += ' layout--no-sidebar';
    } else if (sidebarState.isCollapsed) {
      layoutClass += ' layout--sidebar-collapsed';
    }
    
    return layoutClass;
  }, [sidebarState]);

  // Формируем класс для контейнера
  const getContainerClass = useCallback(() => {
    const containerType = getContainerType();
    let baseClass = 'layout__container';
    
    if (containerType !== 'default') {
      baseClass += ` layout__container--${containerType}`;
    }
    
    return baseClass;
  }, [getContainerType]);

  // Формируем класс для main
  const getMainClass = useCallback(() => {
    let mainClass = 'layout__main';
    
    if (className) {
      mainClass += ` ${className}`;
    }
    
    return mainClass;
  }, [className]);

  return (
    <div className={getLayoutClass()}>
      {/* ✅ GRID AREAS: Header */}
      {showHeader && (
        <div className="layout__header">
          <Header variant={headerVariant} />
        </div>
      )}
      
      {/* ✅ GRID AREAS: Sidebar - передаем состояние и callback */}
      <div className="layout__sidebar">
        <Sidebar 
          isVisible={sidebarState.isVisible}
          isCollapsed={sidebarState.isCollapsed}
          onCollapsedChange={handleSidebarCollapsedChange}
        />
      </div>
      
      {/* ✅ GRID AREAS: Main - автоматически расширяется при сворачивании sidebar */}
      <main className={getMainClass()} style={style}>
        <div className={getContainerClass()} {...containerProps}>
          {loading ? (
            <Loader 
              type="spinner" 
              centered 
              size="md" 
              text={t('common.loading')} 
            />
          ) : (
            children
          )}
        </div>
      </main>
      
      {/* ✅ GRID AREAS: Footer */}
      {showFooter && (
        <div className="layout__footer">
          <Footer />
        </div>
      )}
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  showSidebar: PropTypes.bool,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  headerVariant: PropTypes.oneOf(['default', 'minimal', 'transparent']),
  containerProps: PropTypes.object,
};

/**
 * HOC для создания layout компонентов с предустановленными настройками
 */
export const withLayout = (options = {}) => (WrappedComponent) => {
  const LayoutWrapper = (props) => (
    <Layout {...options}>
      <WrappedComponent {...props} />
    </Layout>
  );
  
  LayoutWrapper.displayName = `withLayout(${WrappedComponent.displayName || WrappedComponent.name})`;
  return LayoutWrapper;
};

/**
 * Готовые layout компоненты для разных типов страниц (теперь НЕ используются)
 */

// Полноширинный layout для дашбордов (только для авторизованных)
export const DashboardLayout = ({ children, ...props }) => (
  <Layout {...props}>
    {children}
  </Layout>
);

DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// Layout для форм и настроек (только для авторизованных)
export const FormLayout = ({ children, ...props }) => (
  <Layout {...props}>
    {children}
  </Layout>
);

FormLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// Layout для контента и статей (без sidebar)
export const ContentLayout = ({ children, ...props }) => (
  <Layout showSidebar={false} {...props}>
    {children}
  </Layout>
);

ContentLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// Layout для страниц аутентификации (без sidebar)
export const AuthLayout = ({ children, ...props }) => (
  <Layout 
    showSidebar={false} 
    showFooter={false}
    headerVariant="minimal"
    {...props}
  >
    {children}
  </Layout>
);

AuthLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// Layout для лендингов (без sidebar)
export const LandingLayout = ({ children, ...props }) => (
  <Layout 
    showSidebar={false}
    headerVariant="transparent"
    {...props}
  >
    {children}
  </Layout>
);

LandingLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

// ✅ Layout для Discovery/Analysis страниц (теперь автоматически)
export const DiscoveryLayout = ({ children, ...props }) => (
  <Layout {...props}>
    {children}
  </Layout>
);

DiscoveryLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Хуки для работы с layout
 */

// Хук для получения информации о текущем layout
export const useLayout = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  const getLayoutType = () => {
    if (location.pathname.startsWith('/dashboard')) return 'dashboard';
    if (location.pathname.startsWith('/settings')) return 'form';
    if (location.pathname.startsWith('/profile')) return 'form';
    if (location.pathname.startsWith('/blog')) return 'content';
    if (location.pathname.startsWith('/docs')) return 'content';
    if (location.pathname.startsWith('/login')) return 'auth';
    if (location.pathname.startsWith('/register')) return 'auth';
    if (location.pathname.startsWith('/video-analysis')) return 'discovery';
    if (location.pathname.startsWith('/channel-analysis')) return 'discovery';
    if (location.pathname.startsWith('/video-discovery')) return 'discovery';
    if (location.pathname.startsWith('/channel-discovery')) return 'discovery';
    if (location.pathname === '/') return 'landing';
    
    return 'default';
  };
  
  const isAuthPage = location.pathname.startsWith('/login') || 
                     location.pathname.startsWith('/register') ||
                     location.pathname.startsWith('/forgot-password');
  
  const isDashboardPage = location.pathname.startsWith('/dashboard');
  const isDiscoveryPage = location.pathname.includes('discovery') || location.pathname.includes('analysis');
  
  return {
    layoutType: getLayoutType(),
    isAuthPage,
    isDashboardPage,
    isDiscoveryPage,
    currentPath: location.pathname,
    isAuthenticated,
    shouldShowSidebar: isAuthenticated && !isAuthPage,
  };
};

// Хук для адаптивных брейкпоинтов
export const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = React.useState('lg');
  
  React.useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 480) setBreakpoint('xs');
      else if (width < 768) setBreakpoint('sm');
      else if (width < 992) setBreakpoint('md');
      else if (width < 1200) setBreakpoint('lg');
      else if (width < 1400) setBreakpoint('xl');
      else setBreakpoint('2xl');
    };
    
    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);
  
  return {
    breakpoint,
    isMobile: breakpoint === 'xs' || breakpoint === 'sm',
    isTablet: breakpoint === 'md',
    isDesktop: breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl',
    isLargeScreen: breakpoint === 'xl' || breakpoint === '2xl',
  };
};

export default Layout;